import { admissionDatabaseRequests } from "@/src/api/admissionOfficeDatabaseRequests";
import { applicantHistoryRequests } from "@/src/api/applicantHistoryRequests";
import { tasksPageRequests } from "@/src/api/tasksPageRequests";
import { tasksStatusConst } from "@/src/constants/tasksConsts";
import { matchAuthDocByPdfPath } from "@/src/utils/admissionOffice/matchAuthDocByPdfPath";
import { parseAuthDocsResponse } from "@/src/utils/admissionOffice/parseAuthDocsResponse";
import { resolveAndDeleteApplicantGenerateDoc } from "@/src/utils/applicantGenerateDocs/resolveAndDeleteApplicantGenerateDoc";
import { resolveAdmissionDocumentIdByAttachedFile } from "@/src/utils/tasks/resolveAdmissionDocumentIdByAttachedFile";

const REJECTED_STATUS = "rejected";

export interface ExecuteDocumentCheckRejectParams {
  taskId: string;
  applicantId?: number;
  fullName?: string;
  attachedFile?: string;
  lookupDocumentIdByPdfPath?: (pdfPath: string) => Promise<number | null>;
}

export type ExecuteDocumentCheckRejectResult =
  | { ok: true; message: string }
  | { ok: false; message: string };

/** Rejects task, deletes attached document from Admissions Office DB, clears `attached_file`. */
export const executeDocumentCheckReject = async ({
  taskId,
  applicantId,
  fullName,
  attachedFile,
  lookupDocumentIdByPdfPath,
}: ExecuteDocumentCheckRejectParams): Promise<ExecuteDocumentCheckRejectResult> => {
  const trimmedFile = attachedFile?.trim() ?? "";
  const trimmedName = fullName?.trim() ?? "";

  if (trimmedFile && trimmedName) {
    let documentId: number | null = null;
    let documentSeria: string | undefined;

    const authRawResponse = await admissionDatabaseRequests.getAuthDocsByFullname(trimmedName);
    const authDocs = parseAuthDocsResponse(authRawResponse);
    const matchedDoc = matchAuthDocByPdfPath(authDocs, trimmedFile);
    if (typeof matchedDoc?.id === "number") {
      documentId = matchedDoc.id;
      documentSeria = matchedDoc.document_seria;
    } else {
      documentId = await resolveAdmissionDocumentIdByAttachedFile(
        trimmedName,
        trimmedFile,
        lookupDocumentIdByPdfPath,
      );
    }

    if (documentId != null) {
      const userOffice = localStorage.getItem("userID") || "without login";
      const deleted = await admissionDatabaseRequests.deleteCurrentDocument(documentId, userOffice);
      if (deleted == null) {
        return { ok: false, message: "Could not delete the document from Admissions Office." };
      }

      try {
        await resolveAndDeleteApplicantGenerateDoc({
          applicantId,
          documentSeria,
        });
      } catch (error) {
        console.warn("[DocumentCheckReject] applicant-generate-docs delete failed:", error);
      }
    } else {
      console.warn("[DocumentCheckReject] Document id not found; updating task only.", trimmedFile);
    }
  }

  await tasksPageRequests.update(taskId, {
    status: REJECTED_STATUS,
    attached_file: trimmedFile ? null : undefined,
  });

  const rejectedLabel = tasksStatusConst.find((item) => item.id === REJECTED_STATUS)?.label ?? REJECTED_STATUS;

  if (applicantId != null && Number.isFinite(applicantId)) {
    try {
      await applicantHistoryRequests.create({
        applicant_id: applicantId,
        text: `Changes have been made to status updated to "${rejectedLabel}"`,
      });
    } catch (error) {
      console.warn("[DocumentCheckReject] History create failed:", error);
    }
  }

  const message = trimmedFile
    ? `Document deleted from Admissions Office. Task marked as "${rejectedLabel}".`
    : `Task marked as "${rejectedLabel}".`;

  return { ok: true, message };
};
